"use client";
import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../utils/axiosInterceptor";
import { useParams, useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  Download,
  Printer,
  Home,
  TrendingUp,
  Award,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Shield,
  Video,
  Bluetooth,
  Activity,
  Camera,
} from "lucide-react";

// Define TypeScript interfaces
interface Result {
  skill: string;
  rating: string;
  conclusion: string;
}

interface Analysis {
  performance: string;
  strengths: string[];
  weaknesses: string[];
  results: Result[];
}

interface InterviewDetails {
  title: string;
  description: string;
  difficulty: string;
}

interface ProctoringData {
  proctoringScore: number;
  faceDetectionViolations: number;
  events: Array<{
    type: string;
    details: any;
    timestamp: string;
  }>;
  audioFlags: Array<{
    type: string;
    timestamp: string;
    details?: any;
  }>;
  screenshots: Array<{
    url: string;
    timestamp: string;
    metadata?: any;
  }>;
  recordings: {
    videoUrl?: string;
    bluetoothDetected?: boolean;
    bluetoothDevices?: string[];
    terminationReason?: string;
    terminatedAt?: string;
  };
}

interface ApiResponse {
  interviewDetails: InterviewDetails;
  analysis: Analysis;
  proctoring: ProctoringData;
  sessionInfo: {
    sessionId: string;
    startTime: string;
    endTime: string;
    duration: number;
    status: string;
    interviewId?: string;
  };
}

// Gradient progress bar component
const RatingBar: React.FC<{ rating: string }> = ({ rating }) => {
  const [score, maxScore] = rating.split("/").map((n) => parseInt(n.trim(), 10) || 0);
  const percentage = useMemo(() => (score / maxScore) * 100, [score, maxScore]);
  const color =
    percentage >= 80
      ? "from-green-500 to-emerald-500"
      : percentage >= 60
      ? "from-yellow-500 to-orange-500"
      : "from-red-500 to-rose-500";

  return (
    <div className="flex items-center space-x-3 w-full">
      <span className="font-bold text-gray-800 w-12 text-right">{rating}</span>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
        <div
          className={`h-3 rounded-full bg-gradient-to-r ${color} transition-all duration-1000 ease-out shadow-md`}
          style={{ width: `${percentage}%` }}
          aria-valuenow={score}
          aria-valuemax={maxScore}
          role="progressbar"
        ></div>
      </div>
      <span className="text-sm font-semibold text-gray-600 w-12">{percentage.toFixed(0)}%</span>
    </div>
  );
};

// Proctoring score indicator
const ProctoringScoreBar: React.FC<{ score: number }> = ({ score }) => {
  const color =
    score >= 80
      ? "from-green-500 to-emerald-500"
      : score >= 60
      ? "from-yellow-500 to-orange-500"
      : "from-red-500 to-rose-500";
  const status =
    score >= 80 ? "Compliant" : score >= 60 ? "Minor Issues" : "Violations Detected";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Proctoring Score</span>
        <span className={`text-lg font-bold ${
          score >= 80 ? "text-green-600" : score >= 60 ? "text-yellow-600" : "text-red-600"
        }`}>
          {score}/100
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
        <div
          className={`h-4 rounded-full bg-gradient-to-r ${color} transition-all duration-1000 ease-out shadow-md`}
          style={{ width: `${score}%` }}
        ></div>
      </div>
      <span className={`text-xs font-semibold ${
        score >= 80 ? "text-green-600" : score >= 60 ? "text-yellow-600" : "text-red-600"
      }`}>
        {status}
      </span>
    </div>
  );
};

// Main component
const InterviewReportPage: React.FC = () => {
  const { sessionid } = useParams<{ sessionid: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let attempt = 0;
    const MAX_RETRIES = 5;
    const apiUrl = `/interview/generate-results?sessionId=${sessionid}`;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      while (attempt < MAX_RETRIES) {
        try {
          const response = await axiosInstance.get<ApiResponse>(apiUrl);
          
          // The API already returns data in the correct format
          setData(response.data);
          setIsLoading(false);
          return;
        } catch (err: any) {
          attempt++;
          console.error(`Attempt ${attempt} failed:`, err);
          if (attempt >= MAX_RETRIES) {
            setError(err.response?.data?.error || err.message || "Failed to load interview report after multiple retries.");
            setIsLoading(false);
          } else {
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
    };

    fetchData();
  }, [sessionid]);

  // Calculate overall score
  const overallScore = useMemo(() => {
    if (!data?.analysis?.results) return 0;
    const totalScore = data.analysis.results.reduce((acc, result) => {
      const [score, max] = result.rating.split("/").map((n) => parseInt(n.trim(), 10) || 0);
      return acc + (score / max) * 100;
    }, 0);
    return Math.round(totalScore / data.analysis.results.length);
  }, [data]);

  // Prepare data for charts
  const radarData = useMemo(() => {
    if (!data?.analysis?.results) return [];
    return data.analysis.results.map((result) => {
      const [score, max] = result.rating.split("/").map((n) => parseInt(n.trim(), 10) || 0);
      return {
        skill: result.skill.length > 15 ? result.skill.substring(0, 15) + "..." : result.skill,
        fullSkill: result.skill,
        score: (score / max) * 100,
      };
    });
  }, [data]);

  const pieData = useMemo(() => {
    if (!data?.analysis?.results) return [];
    return data.analysis.results.map((result) => {
      const [score] = result.rating.split("/").map((n) => parseInt(n.trim(), 10) || 0);
      return {
        name: result.skill,
        value: score,
      };
    });
  }, [data]);

  const barData = useMemo(() => {
    if (!data?.analysis?.results) return [];
    return data.analysis.results.map((result) => {
      const [score, max] = result.rating.split("/").map((n) => parseInt(n.trim(), 10) || 0);
      return {
        skill: result.skill.length > 20 ? result.skill.substring(0, 20) + "..." : result.skill,
        score: score,
        percentage: (score / max) * 100,
      };
    });
  }, [data]);

  // Proctoring event timeline data
  const eventTimelineData = useMemo(() => {
    if (!data?.proctoring?.events) return [];
    const eventCounts: { [key: string]: number } = {};
    data.proctoring.events.forEach((event) => {
      const hour = new Date(event.timestamp).getHours();
      const key = `${hour}:00`;
      eventCounts[key] = (eventCounts[key] || 0) + 1;
    });
    return Object.entries(eventCounts).map(([time, count]) => ({ time, count }));
  }, [data]);

  const COLORS = ["#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444"];

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const reportData = JSON.stringify(data, null, 2);
    const blob = new Blob([reportData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `interview-report-${sessionid}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadVideo = () => {
    if (data?.proctoring?.recordings?.videoUrl || data?.sessionInfo?.sessionId) {
      // Use the recording endpoint we created
      const videoUrl = data.proctoring.recordings.videoUrl || 
        `/interview/recording/${data.sessionInfo.sessionId}?interviewId=${data.sessionInfo.interviewId || ''}`;
      window.open(videoUrl, "_blank");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-red-50 to-red-100">
        <div className="text-center p-8 bg-white rounded-2xl shadow-2xl border-t-4 border-red-500 max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-red-600 mb-3">Error Loading Report</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="relative">
            <div className="w-24 h-24 border-8 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
            <Clock className="w-8 h-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Analyzing Interview Performance</h2>
          <p className="text-gray-600">AI is generating your comprehensive report...</p>
          <div className="mt-6 flex justify-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
          </div>
        </div>
      </div>
    );
  }

  const { interviewDetails, analysis, proctoring, sessionInfo } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-8 print:bg-white">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-2xl p-8 mb-8 text-white print:bg-blue-600">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-extrabold mb-2 flex items-center">
                <Award className="w-10 h-10 mr-3" />
                Interview Performance Report
              </h1>
              <p className="text-xl text-blue-100 mb-4">
                <span className="font-semibold">{interviewDetails.title}</span> - Comprehensive Assessment
              </p>
              <div className="flex items-center space-x-4 text-sm text-blue-100">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Session ID: {String(sessionid).substring(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>{new Date(sessionInfo.startTime).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4" />
                  <span>Duration: {Math.round(sessionInfo.duration / 60000)} min</span>
                </div>
              </div>
            </div>

            {/* Actions - Hide on print */}
            <div className="flex space-x-3 print:hidden">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg flex items-center space-x-2 transition-all"
              >
                <Printer className="w-5 h-5" />
                <span>Print</span>
              </button>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg flex items-center space-x-2 transition-all"
              >
                <Download className="w-5 h-5" />
                <span>Download</span>
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg flex items-center space-x-2 transition-all"
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </button>
            </div>
          </div>

          {/* Overall Score Badge */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100 mb-1">Overall Performance Score</p>
                <div className="flex items-baseline space-x-2">
                  <span className="text-6xl font-extrabold">{overallScore}%</span>
                  <TrendingUp className="w-8 h-8 text-green-300" />
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-100 mb-1">Assessment Level</p>
                <span
                  className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                    overallScore >= 80 ? "bg-green-500" : overallScore >= 60 ? "bg-yellow-500" : "bg-red-500"
                  } text-white`}
                >
                  {overallScore >= 80 ? "Excellent" : overallScore >= 60 ? "Good" : "Needs Improvement"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Job Details Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-indigo-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                <Award className="w-6 h-6 text-indigo-600" />
              </div>
              Position Details
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 mb-1">Job Role</p>
                <p className="font-semibold text-gray-900">{interviewDetails.title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Difficulty Level</p>
                <span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-indigo-100 text-indigo-800">
                  {interviewDetails.difficulty}
                </span>
              </div>
              <details className="text-gray-600 text-sm cursor-pointer mt-4 pt-4 border-t border-gray-200">
                <summary className="font-semibold text-gray-700 hover:text-indigo-600 transition-colors">
                  View Full Description
                </summary>
                <div className="mt-3 text-gray-600 max-h-48 overflow-y-auto">
                  {interviewDetails.description.split("\n").map((line, index) => (
                    <p key={index} className="mb-1">
                      {line}
                    </p>
                  ))}
                </div>
              </details>
            </div>
          </div>

          {/* Performance Summary Card */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6 border-t-4 border-emerald-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              Overall Assessment
            </h2>
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border-l-4 border-emerald-500">
              <p className="text-gray-700 leading-relaxed text-lg italic">&ldquo;{analysis.performance}&rdquo;</p>
            </div>
            <div className="mt-4 flex justify-end">
              <span
                className={`inline-flex items-center px-6 py-2 text-sm font-bold rounded-full shadow-lg ${
                  overallScore >= 80
                    ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                    : overallScore >= 60
                    ? "bg-gradient-to-r from-yellow-500 to-orange-600 text-white"
                    : "bg-gradient-to-r from-red-500 to-rose-600 text-white"
                }`}
              >
                <Award className="w-4 h-4 mr-2" />
                {overallScore >= 80
                  ? "Strong Candidate - Recommended"
                  : overallScore >= 60
                  ? "Promising Candidate - Consider"
                  : "Needs Development - Follow-up Required"}
              </span>
            </div>
          </div>
        </div>

        {/* Proctoring Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-t-4 border-purple-500">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center text-purple-700">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            Proctoring & Integrity Assessment
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Proctoring Score */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-purple-600" />
                  Proctoring Score
                </h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    proctoring.proctoringScore >= 80
                      ? "bg-green-100 text-green-700"
                      : proctoring.proctoringScore >= 60
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {proctoring.proctoringScore >= 80
                    ? "Compliant"
                    : proctoring.proctoringScore >= 60
                    ? "Minor Issues"
                    : "Violations"}
                </span>
              </div>
              <ProctoringScoreBar score={proctoring.proctoringScore} />
            </div>

            {/* Violations Summary */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-6 border border-red-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                Violations Detected
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Face Detection Violations</span>
                  <span className="text-lg font-bold text-red-600">
                    {proctoring.faceDetectionViolations}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Total Events</span>
                  <span className="text-lg font-bold text-gray-800">{proctoring.events.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Audio Flags</span>
                  <span className="text-lg font-bold text-gray-800">{proctoring.audioFlags.length}</span>
                </div>
                {proctoring.recordings.bluetoothDetected && (
                  <div className="flex items-center justify-between bg-red-100 p-2 rounded">
                    <span className="text-sm font-semibold text-red-700">Bluetooth Detected</span>
                    <Bluetooth className="w-5 h-5 text-red-600" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Proctoring Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Events Timeline */}
            {eventTimelineData.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-blue-600" />
                  Event Timeline
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={eventTimelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Events Summary */}
            {proctoring.events.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-blue-600" />
                  Events Summary
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(() => {
                    // Group events by type and calculate total duration
                    const eventGroups: { [key: string]: { count: number; timestamps: string[] } } = {};
                    proctoring.events.forEach((event) => {
                      if (!eventGroups[event.type]) {
                        eventGroups[event.type] = { count: 0, timestamps: [] };
                      }
                      eventGroups[event.type].count++;
                      eventGroups[event.type].timestamps.push(event.timestamp);
                    });

                    // Calculate duration for each event type (time between first and last occurrence)
                    const eventSummary = Object.entries(eventGroups).map(([type, data]) => {
                      const timestamps = data.timestamps.map(ts => new Date(ts).getTime()).sort((a, b) => a - b);
                      const duration = timestamps.length > 1 
                        ? Math.round((timestamps[timestamps.length - 1] - timestamps[0]) / 1000)
                        : 0;
                      return { type, count: data.count, duration };
                    });

                    return eventSummary.slice(0, 10).map((event, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <Activity className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-700">
                            {event.type.replace(/_/g, ' ')} ({event.count}x)
                          </span>
                        </div>
                        {event.duration > 0 && (
                          <span className="text-xs text-gray-500">
                            {event.duration}sec
                          </span>
                        )}
                      </div>
                    ));
                  })()}
                  {proctoring.events.length > 10 && (
                    <p className="text-xs text-gray-500 text-center">
                      +{proctoring.events.length - 10} more events
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Detailed Events Table - Collapsible */}
          {proctoring.events.length > 0 && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 mt-6">
              <details className="group">
                <summary className="cursor-pointer list-none flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-blue-600" />
                    Detailed Events Log ({proctoring.events.length} events)
                  </h3>
                  <span className="text-sm text-gray-500 group-open:hidden">Click to expand</span>
                  <span className="text-sm text-gray-500 hidden group-open:inline">Click to collapse</span>
                </summary>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Event Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {proctoring.events
                        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                        .map((event, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {new Date(event.timestamp).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {event.type.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {event.details && Object.keys(event.details).length > 0 ? (
                                <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(event.details, null, 2)}
                                </pre>
                              ) : (
                                <span className="text-gray-400">No details</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}

          {/* Screenshots Section */}
          {proctoring.screenshots && proctoring.screenshots.length > 0 && (
            <div className="bg-white rounded-lg p-6 border border-gray-200 mt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2 text-purple-600" />
                Proctoring Screenshots ({proctoring.screenshots.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {proctoring.screenshots.map((screenshot, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={screenshot.url}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(screenshot.url, "_blank")}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23ddd' width='100' height='100'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EFailed to load%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-gray-500 text-center">
                      {new Date(screenshot.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Termination Info */}
          {proctoring.recordings.terminationReason && (
            <div className="mt-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <XCircle className="w-6 h-6 text-red-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-bold text-red-800 mb-1">Session Terminated</h4>
                  <p className="text-sm text-red-700 mb-2">
                    Reason: {proctoring.recordings.terminationReason}
                  </p>
                  {proctoring.recordings.terminatedAt && (
                    <p className="text-xs text-red-600">
                      Terminated at: {new Date(proctoring.recordings.terminatedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Video Recording */}
          {(proctoring.recordings.videoUrl || sessionInfo.sessionId) && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Video className="w-6 h-6 text-blue-600" />
                  <div>
                    <h4 className="font-bold text-gray-800">Interview Recording Available</h4>
                    <p className="text-sm text-gray-600">Full session video recording</p>
                  </div>
                </div>
                <button
                  onClick={handleDownloadVideo}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 print:hidden"
                >
                  <Download className="w-4 h-4" />
                  <span>View Recording</span>
                </button>
              </div>
            </div>
          )}

          {/* Bluetooth Devices */}
          {proctoring.recordings.bluetoothDevices && proctoring.recordings.bluetoothDevices.length > 0 && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Bluetooth className="w-5 h-5 text-yellow-600" />
                <h4 className="font-bold text-gray-800">Bluetooth Devices Detected</h4>
              </div>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                {proctoring.recordings.bluetoothDevices.map((device, index) => (
                  <li key={index}>{device}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8 print:hidden">
          {/* Radar Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Skills Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="Performance" dataKey="score" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Score Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${(name || '').substring(0, 15)}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart - Full Width */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 print:hidden">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Performance Metrics</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="skill" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 5]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="score" fill="#3B82F6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Strengths and Weaknesses */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strengths */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-green-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center text-green-700">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              Key Strengths
            </h2>
            <ul className="space-y-3">
              {analysis.strengths.length > 0 ? (
                analysis.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start bg-green-50 p-3 rounded-lg transition-all hover:shadow-md">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium">{strength}</span>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 italic">No specific strengths identified</li>
              )}
            </ul>
          </div>

          {/* Weaknesses */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-amber-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center text-amber-700">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              Areas for Development
            </h2>
            <ul className="space-y-3">
              {analysis.weaknesses.length > 0 ? (
                analysis.weaknesses.map((weakness, index) => (
                  <li key={index} className="flex items-start bg-amber-50 p-3 rounded-lg transition-all hover:shadow-md">
                    <div className="flex-shrink-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{weakness}</span>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 italic">No major weaknesses identified</li>
              )}
            </ul>
          </div>
        </div>

        {/* Detailed Skill Ratings */}
        {analysis.results.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-purple-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center text-purple-700">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              Detailed Skill Assessment
            </h2>

            <div className="space-y-6">
              {analysis.results.map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex flex-col space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center">
                        <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold mr-3">
                          {index + 1}
                        </span>
                        {result.skill}
                      </h3>
                    </div>
                    <RatingBar rating={result.rating} />
                    <p className="text-sm text-gray-600 italic pl-11 pt-2 border-l-2 border-purple-200 ml-4">
                      {result.conclusion}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm pb-8">
          <p>This report was generated on {new Date().toLocaleString()}</p>
          <p className="mt-1">Interview Session ID: {String(sessionid)}</p>
          <p className="mt-4 text-xs">© 2024 EarlyJobs AI Interview System - Confidential</p>
        </div>
      </div>
    </div>
  );
};

export default InterviewReportPage;

